import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { WaItem, WA_ITEM_ROLE } from '../wa-tools/wa-item-class.js';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

// Créer le contexte
const WaItemContext = createContext();

// Hook pour utiliser le contexte
export const useWaItem = () => {
  const context = useContext(WaItemContext);
  if (!context) {
    throw new Error('useWaItem must be used within WaItemProvider');
  }
  return context;
};

// Provider du contexte
export const WaItemProvider = ({ children }) => {
  const [mainWaItem, setMainWaItem] = useState(null);
  const [waItems, setWaItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fonction pour initialiser le waItem Main avec destruction/recréation
  const initializeMainWaItem = useCallback(async () => {
    debug.log(config, 'WaItemContext: Initialisation du waItem Main (destruction/recréation)');
    
    setLoading(true);
    // Destruction explicite de l'ancienne instance
    setMainWaItem(null);
    
    try {
      // Recréation d'une nouvelle instance
      const newMainItem = new WaItem(WA_ITEM_ROLE.MAIN, true);
      
      // Chargement des données
      await newMainItem.refreshData();
      
      // Mise à jour de l'état
      setMainWaItem(newMainItem);
      debug.log(config, 'WaItemContext: waItem Main initialisé avec succès');
    } catch (error) {
      debug.error(config, 'WaItemContext: Erreur lors de l\'initialisation du waItem Main:', error.message);
      setMainWaItem(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour rafraîchir avec destruction/recréation
  const refreshMainWaItem = useCallback(async () => {
    if (loading) {
      debug.log(config, 'WaItemContext: Chargement déjà en cours, ignorer');
      return;
    }

    debug.log(config, 'WaItemContext: Rafraîchissement du waItem Main (destruction/recréation)');
    
    setLoading(true);
    // Destruction explicite de l'ancienne instance
    const oldMainWaItem = mainWaItem;
    setMainWaItem(null);
    
    try {
      // Recréation d'une nouvelle instance
      const newMainItem = new WaItem(WA_ITEM_ROLE.MAIN, true);
      
      // Si on avait un ID précédent, on peut le réutiliser
      if (oldMainWaItem && oldMainWaItem.id) {
        newMainItem.id = oldMainWaItem.id;
      }
      
      // Chargement des données
      await newMainItem.refreshData();
      
      // Mise à jour de l'état
      setMainWaItem(newMainItem);
      debug.log(config, 'WaItemContext: waItem Main rafraîchi avec succès');
    } catch (error) {
      debug.error(config, 'WaItemContext: Erreur lors du rafraîchissement du waItem Main:', error.message);
      setMainWaItem(null);
    } finally {
      setLoading(false);
    }
  }, [mainWaItem, loading]);

  // Fonction pour ajouter un waItem (avec useCallback)
  const addWaItem = useCallback((waItem) => {
    debug.log(config, 'WaItemContext: Ajout d\'un waItem:', waItem.role);
    setWaItems(prevItems => [...prevItems, waItem]);
  }, []);

  // Fonction pour supprimer un waItem (avec useCallback)
  const removeWaItem = useCallback((waItemToRemove) => {
    debug.log(config, 'WaItemContext: Suppression d\'un waItem:', waItemToRemove.role);
    setWaItems(prevItems => prevItems.filter(item => item !== waItemToRemove));
  }, []);

  // Fonction pour obtenir un waItem par rôle (avec useCallback)
  const getWaItemByRole = useCallback((role) => {
    if (role === WA_ITEM_ROLE.MAIN) {
      return mainWaItem;
    }
    return waItems.find(item => item.role === role);
  }, [mainWaItem, waItems]);

  // Fonction pour vider tous les waItems (avec useCallback)
  const clearAllWaItems = useCallback(() => {
    debug.log(config, 'WaItemContext: Suppression de tous les waItems');
    setMainWaItem(null);
    setWaItems([]);
  }, []);

  return (
    <WaItemContext.Provider value={{
      // État actuel
      mainWaItem,
      waItems,
      loading,
      
      // Actions
      initializeMainWaItem,
      refreshMainWaItem,
      addWaItem,
      removeWaItem,
      getWaItemByRole,
      clearAllWaItems,
      setMainWaItem,
      setWaItems
    }}>
      {children}
    </WaItemContext.Provider>
  );
};
